'use client';

import { useEffect, useId, useMemo } from 'react';
import type { CSSProperties, FC } from 'react';
import { Input } from '@mantine/core';
import type { RichTextEditorProps as MantineRichTextEditorProps } from '@mantine/tiptap';
import { RichTextEditor as MantineRichTextEditor } from '@mantine/tiptap';
import { Color } from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { useEditor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';

import classes from './rich-text-editor.module.css';

const COLORS = [
  '#25262b',
  '#868e96',
  '#fa5252',
  '#e64980',
  '#be4bdb',
  '#7950f2',
  '#4c6ef5',
  '#228be6',
  '#15aabf',
  '#12b886',
  '#40c057',
  '#82c91e',
  '#fab005',
  '#fd7e14',
];

export interface RichTextEditorProps {
  className?: string;
  onBlur?: (rawText: string, htmlContent: string) => void;
  onChange: (rawText: string, htmlContent: string) => void;
  value: string;
  isFormDisabled?: boolean;
  label?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  mih?: MantineRichTextEditorProps['mih'];
  miw?: MantineRichTextEditorProps['miw'];
  /**
   * Use bubble menu instead of fixed toolbar.
   * When true, formatting controls appear in a floating menu on text selection.
   * @default false
   */
  useBubbleMenu?: boolean;
}

export const RichTextEditor: FC<RichTextEditorProps> = (props) => {
  const {
    className,
    onBlur,
    onChange,
    value,
    isFormDisabled = false,
    label,
    placeholder = '',
    error,
    required,
    mih = '7rem',
    miw,
    useBubbleMenu = false,
  } = props;

  // Pinned explicitly rather than left to Input.Wrapper's own id template, so the
  // aria references below can't silently drift from the elements they point at.
  const id = useId();
  const labelId = `${id}-label`;
  const errorId = `${id}-error`;

  // Applied to the contenteditable element so screen readers announce the
  // field's label, required state, and validation errors. Input.Wrapper can't
  // wire these itself — the editor is a contenteditable, not a form control.
  const editorAttributes = useMemo(
    () => ({
      ...(label && { 'aria-labelledby': labelId }),
      ...(required && { 'aria-required': 'true' }),
      ...(error && { 'aria-invalid': 'true', 'aria-describedby': errorId }),
    }),
    [label, labelId, required, error, errorId],
  );

  const editor = useEditor({
    // StarterKit bundles the Underline mark in tiptap v3
    extensions: [
      StarterKit,
      Color,
      TextStyle,
      Placeholder.configure({
        placeholder,
      }),
    ],
    editable: !isFormDisabled,
    editorProps: {
      attributes: editorAttributes,
    },
    content: value,
    // tiptap v3 renders on the server by default; opt out to avoid Next.js hydration mismatches
    immediatelyRender: false,
    onUpdate(ctx) {
      onChange(ctx.editor.getText(), ctx.editor.getHTML());
    },
  });

  // Keep aria attributes in sync when `error` (or other props) change after mount.
  useEffect(() => {
    editor?.setOptions({ editorProps: { attributes: editorAttributes } });
  }, [editor, editorAttributes]);

  useEffect(() => {
    if (!value) {
      // clear if value is cleared(click new project from details page); don't emit —
      // echoing onChange('<p></p>') here would overwrite the parent's cleared value
      editor?.commands.clearContent(false);
    } else if (editor?.getHTML() !== value) {
      // if value change isn't from editing the input; tiptap v3 setContent emits
      // an update by default, which would loop back into onChange
      editor?.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  //TODO: Fix dependencies on useEffect. See CP-3913.
  useEffect(() => {
    // make un-editable when submitting project

    editor?.setEditable(!isFormDisabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormDisabled]);

  const handleBlur = () => {
    onBlur?.(editor?.getText() ?? '', editor?.getHTML() ?? '');
  };

  return (
    <Input.Wrapper
      id={id}
      label={label}
      required={required}
      error={error}
      // The editor is a contenteditable, not a form control, so a `for` on the
      // label would dangle; aria-labelledby does the association instead.
      labelElement="div"
      labelProps={{ id: labelId }}
      // Input.Error is not a live region by default, and its zero margin only
      // works out for a real Input (which supplies --input-margin-bottom).
      errorProps={{
        id: errorId,
        mt: 'xs',
        role: 'alert',
        'aria-live': 'polite',
        'aria-atomic': true,
      }}
    >
      <MantineRichTextEditor
        className={className}
        editor={editor}
        onBlur={handleBlur}
        mih={mih}
        miw={miw}
        classNames={{
          root: classes.root,
          toolbar: classes.toolbar,
        }}
        style={
          {
            '--rte-border-color': error
              ? 'var(--mantine-color-error)'
              : 'var(--mantine-color-placeholder)',
          } as CSSProperties
        }
      >
        <MantineRichTextEditor.Content />

        {!isFormDisabled &&
          (useBubbleMenu && editor ? (
            <BubbleMenu editor={editor}>
              <MantineRichTextEditor.ControlsGroup>
                <MantineRichTextEditor.Bold />
                <MantineRichTextEditor.Italic />
                <MantineRichTextEditor.Underline />
                <MantineRichTextEditor.Strikethrough />
                <MantineRichTextEditor.H1 />
                <MantineRichTextEditor.H2 />
                <MantineRichTextEditor.H3 />
                <MantineRichTextEditor.H4 />
                <MantineRichTextEditor.H5 />
                <MantineRichTextEditor.H6 />
                <MantineRichTextEditor.ColorPicker colors={COLORS} />
                <MantineRichTextEditor.BulletList />
                <MantineRichTextEditor.OrderedList />
              </MantineRichTextEditor.ControlsGroup>
            </BubbleMenu>
          ) : (
            <MantineRichTextEditor.Toolbar>
              <MantineRichTextEditor.ControlsGroup>
                <MantineRichTextEditor.Bold />
                <MantineRichTextEditor.Italic />
                <MantineRichTextEditor.Underline />
                <MantineRichTextEditor.Strikethrough />
              </MantineRichTextEditor.ControlsGroup>

              <MantineRichTextEditor.ControlsGroup>
                <MantineRichTextEditor.H1 />
                <MantineRichTextEditor.H2 />
                <MantineRichTextEditor.H3 />
                <MantineRichTextEditor.H4 />
                <MantineRichTextEditor.H5 />
                <MantineRichTextEditor.H6 />
              </MantineRichTextEditor.ControlsGroup>

              <MantineRichTextEditor.ColorPicker colors={COLORS} />

              <MantineRichTextEditor.ControlsGroup>
                <MantineRichTextEditor.BulletList />
                <MantineRichTextEditor.OrderedList />
              </MantineRichTextEditor.ControlsGroup>
            </MantineRichTextEditor.Toolbar>
          ))}
      </MantineRichTextEditor>
    </Input.Wrapper>
  );
};
